import React, { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface CalendarWidgetProps {
  className?: string;
}

export default function CalendarWidget({ className = '' }: CalendarWidgetProps) {
  const [calendarUrl, setCalendarUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [iframeKey, setIframeKey] = useState(0);

  useEffect(() => {
    const fetchCalendarUrl = async () => {
      try {
        setError(null);
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setError('Not authenticated');
          return;
        }

        const { data } = await supabase
          .from('profiles')
          .select('calendar_url')
          .eq('user_id', user.id)
          .single();

        if (data?.calendar_url) {
          // Ensure URL is properly formatted for embedding
          let url = data.calendar_url;
          if (!url.includes('embed')) {
            // Convert sharing URL to embed URL
            url = url.replace('calendar/u/0', 'calendar/embed')
                    .replace('calendar/b/0', 'calendar/embed')
                    .replace('calendar/', 'calendar/embed/');
          }
          setCalendarUrl(url);
        }
      } catch (error) {
        console.error('Error fetching calendar URL:', error);
        setError('Failed to load calendar');
      } finally {
        setLoading(false);
      }
    };

    fetchCalendarUrl();
  }, []);

  if (error) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 ${className}`}>
        <div className="text-center text-red-600 dark:text-red-400">
          <p>Failed to load calendar. Please try refreshing the page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 ${className}`}>
        <div className="animate-pulse flex space-x-4">
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!calendarUrl) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 ${className}`}>
        <div className="text-center">
          <Calendar className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No Calendar Connected</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Add your Google Calendar URL in settings to see your schedule here.
          </p>
          <div className="mt-6">
            <button
              type="button"
              onClick={() => window.location.href = '/settings'}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <Calendar className="h-5 w-5 mr-2" />
              Connect Calendar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg ${className}`}>
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Calendar</h3>
        {error && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
      </div>
      <div className="overflow-hidden relative">
        <iframe
          key={iframeKey}
          src={calendarUrl}
          style={{ border: 0 }}
          width="100%"
          height="600"
          frameBorder="0"
          scrolling="no"
          className="bg-white w-full"
          onError={() => {
            setError('Failed to load calendar');
            // Retry loading once
            setIframeKey(prev => prev + 1);
          }}
          title="Google Calendar"
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        ></iframe>
      </div>
    </div>
  );
}